/**
 * The SEO head for one location's page: title, description, image and canonical URL from the
 * location's Contentful fields, plus a schema.org `LocalBusiness` JSON-LD block in English. It
 * then renders the page body, `./IndividualLocation.tsx`.
 *
 * Position: rendered by the three location pages,
 * `src/pages/locations/united-states/[stateUrlLabel]/[locationSlug].tsx`,
 * `src/pages/locations/canada/[provinceUrlLabel]/[locationSlug].tsx` and
 * `src/pages/locations/mexico/[locationSlug].tsx`, each passing the build-time result of
 * `src/contentful/location/getContentfulLocationDetailsByLocationSlug.ts`. The head is written by
 * `components/Shared/model/MetaHead.tsx`.
 *
 * Rules a change must keep:
 * - The canonical path is rebuilt here from the location's Contentful `country`, `state` and
 *   `slug`: lowercased, with the first space replaced by a hyphen, and no state segment for
 *   Mexico. The real page path uses the header data's `urlLabel` instead (route tree in
 *   `src/pages/locations/index.tsx`). The two agree only while that rule turns the Contentful
 *   text into the `urlLabel`; a name with two spaces would not. `./LocationsOnMap.tsx` builds
 *   its pin links the same way, and
 *   `components/FamilyOfCompanies/FamilyOfCompaniesRefactor/Company.tsx` → `getLocationUrl`
 *   replaces every run of whitespace.
 * - A location with no `country` or no `state` (outside Mexico) gets the canonical `/locations`.
 *
 * As the code stands: the JSON-LD block is filled only when the visitor's language is English;
 * in French or Spanish the page carries an empty `{}` block. Its `url` is always
 * `https://ryerson.com` plus the canonical path, whatever the brand or `NEXT_PUBLIC_ENV_URL`, and
 * `opens` / `closes` are the Contentful hours strings as entered (for example `"8:00am"`).
 */
import React from "react";
import IndividualLocation from "@components/Locations/IndividualLocation";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { allContentfulHeaderCalls } from "@ryerson/frontend.contentful";
import { ProcessedContentfulLocationAllFieldsByLanguage } from "@contentful/location/types";

/**
 * Never called: Next.js runs `getStaticProps` only from files under `src/pages/`, and nothing
 * imports this one. The location pages fetch their own header data.
 */
export const getStaticProps = async () => {
	return await allContentfulHeaderCalls();
};

/** `contentfulLocationByLanguage`: one location in `en`, `fr` and `es`. */
interface IndividualLocationContainerProps {
	contentfulLocationByLanguage: ProcessedContentfulLocationAllFieldsByLanguage;
}

/**
 * Writes the location's SEO head and JSON-LD, then renders {@link IndividualLocation}. Picks the
 * visitor's language from `useApplication().localization.language`, in the browser.
 */
const IndividualLocationContainer: React.FC<IndividualLocationContainerProps> = ({
	contentfulLocationByLanguage,
}) => {
	const {
		localization: { language },
	} = useApplication();

	let metaObj: LanguageContent<MetaContent> = {
		en: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
		fr: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
		es: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
	};
	const contentfuLocationForLanguage = contentfulLocationByLanguage[language];

	// Must equal the page's real path; see "Rules a change must keep" in the file header.
	let canonical = "/locations";
	if (contentfuLocationForLanguage.country === "Mexico") {
		canonical +=
			"/" +
			contentfuLocationForLanguage.country.toLowerCase().replace(" ", "-") +
			"/" +
			contentfuLocationForLanguage.slug;
	} else {
		if (contentfuLocationForLanguage.country && contentfuLocationForLanguage.state) {
			canonical +=
				"/" +
				contentfuLocationForLanguage.country.toLowerCase().replace(" ", "-") +
				"/" +
				contentfuLocationForLanguage.state.toLowerCase().replace(" ", "-") +
				"/" +
				contentfuLocationForLanguage.slug;
		}
	}
	let metaObjForLanguage: MetaContent = {
		title: contentfuLocationForLanguage.seoTitle ?? "",
		description: contentfuLocationForLanguage.seoDescription ?? "",
		imageUrl: contentfuLocationForLanguage.locationPicture?.file.url,
		url: canonical,
		company: false,
	};

	// Only the visitor's language slot of `metaObj` is filled, and only that slot is rendered.
	// `node_locale` is `en-US`, `fr-CA` or `es-MX`; anything that isn't English or French is
	// treated as Spanish.
	let structuredContent: any = {};
	if (contentfuLocationForLanguage.node_locale.split("-").shift() === "en") {
		metaObj.en = metaObjForLanguage;
		structuredContent = {
			"@context": "https://schema.org",
			"@type": "LocalBusiness",
			name: contentfuLocationForLanguage.locationName,
			image: contentfuLocationForLanguage.locationPicture?.file.url,
			url: "https://ryerson.com" + canonical,
			telephone: contentfuLocationForLanguage.phoneNumber,
			address: {
				"@type": "PostalAddress",
				streetAddress: contentfuLocationForLanguage.streetAddress,
				addressLocality: contentfuLocationForLanguage.city,
				addressRegion: contentfuLocationForLanguage.state,
				postalCode: contentfuLocationForLanguage.zipCode,
				addressCountry: contentfuLocationForLanguage.country,
			},
			geo: {
				"@type": "GeoCoordinates",
				latitude: contentfuLocationForLanguage.location.lat,
				longitude: contentfuLocationForLanguage.location.lon,
			},
		};
		// One entry per day that has both an opening and a closing time; a day with either missing
		// is left out, as `IndividualLocation.tsx` → `HoursLine` hides it on the page.
		if (contentfuLocationForLanguage.locationHours) {
			structuredContent.openingHoursSpecification = [];
			if (
				contentfuLocationForLanguage.locationHours.sundayOpen &&
				contentfuLocationForLanguage.locationHours.sundayClose
			) {
				structuredContent.openingHoursSpecification.push({
					"@type": "OpeningHoursSpecification",
					dayOfWeek: ["Sunday"],
					opens: contentfuLocationForLanguage.locationHours.sundayOpen,
					closes: contentfuLocationForLanguage.locationHours.sundayClose,
				});
			}
			if (
				contentfuLocationForLanguage.locationHours.mondayOpen &&
				contentfuLocationForLanguage.locationHours.mondayClose
			) {
				structuredContent.openingHoursSpecification.push({
					"@type": "OpeningHoursSpecification",
					dayOfWeek: ["Monday"],
					opens: contentfuLocationForLanguage.locationHours.mondayOpen,
					closes: contentfuLocationForLanguage.locationHours.mondayClose,
				});
			}
			if (
				contentfuLocationForLanguage.locationHours.tuesdayOpen &&
				contentfuLocationForLanguage.locationHours.tuesdayClose
			) {
				structuredContent.openingHoursSpecification.push({
					"@type": "OpeningHoursSpecification",
					dayOfWeek: ["Tuesday"],
					opens: contentfuLocationForLanguage.locationHours.tuesdayOpen,
					closes: contentfuLocationForLanguage.locationHours.tuesdayClose,
				});
			}
			if (
				contentfuLocationForLanguage.locationHours.wednesdayOpen &&
				contentfuLocationForLanguage.locationHours.wednesdayClose
			) {
				structuredContent.openingHoursSpecification.push({
					"@type": "OpeningHoursSpecification",
					dayOfWeek: ["Wednesday"],
					opens: contentfuLocationForLanguage.locationHours.wednesdayOpen,
					closes: contentfuLocationForLanguage.locationHours.wednesdayClose,
				});
			}
			if (
				contentfuLocationForLanguage.locationHours.thursdayOpen &&
				contentfuLocationForLanguage.locationHours.thursdayClose
			) {
				structuredContent.openingHoursSpecification.push({
					"@type": "OpeningHoursSpecification",
					dayOfWeek: ["Thursday"],
					opens: contentfuLocationForLanguage.locationHours.thursdayOpen,
					closes: contentfuLocationForLanguage.locationHours.thursdayClose,
				});
			}
			if (
				contentfuLocationForLanguage.locationHours.fridayOpen &&
				contentfuLocationForLanguage.locationHours.fridayClose
			) {
				structuredContent.openingHoursSpecification.push({
					"@type": "OpeningHoursSpecification",
					dayOfWeek: ["Friday"],
					opens: contentfuLocationForLanguage.locationHours.fridayOpen,
					closes: contentfuLocationForLanguage.locationHours.fridayClose,
				});
			}
			if (
				contentfuLocationForLanguage.locationHours.saturdayOpen &&
				contentfuLocationForLanguage.locationHours.saturdayClose
			) {
				structuredContent.openingHoursSpecification.push({
					"@type": "OpeningHoursSpecification",
					dayOfWeek: ["Saturday"],
					opens: contentfuLocationForLanguage.locationHours.saturdayOpen,
					closes: contentfuLocationForLanguage.locationHours.saturdayClose,
				});
			}
		}
	} else if (contentfuLocationForLanguage.node_locale.split("-").shift() === "fr") {
		metaObj.fr = metaObjForLanguage;
	} else {
		metaObj.es = metaObjForLanguage;
	}

	return (
		<>
			{/* NOTE: the metaObj[language] will not work for SEO for following reasons:
				1. HasMounted blocks server render, so HTML (including meta tags) is empty → bad for SEO
				2. Localization comes from useState + useContext, which only works after hydration
				3. Meta tags set via metaObj[language] rely on client-only state, so not seen by crawlers
				
				For SSG and SEO, we will need:
				1. Remove HasMounted and address the underlying issue
				2. Determine how to handle localization when deciding what crawlers should see in the meta tags
				3. Pass localization as a prop, not via useContext
			*/}
			<MetaHead content={metaObj[language]}>
				<script type="application/ld+json">{JSON.stringify(structuredContent)}</script>
			</MetaHead>
			<IndividualLocation contentfulLocationByLanguage={contentfulLocationByLanguage} />
		</>
	);
};

export default IndividualLocationContainer;
